import React, { useState } from "react";
import {
  IconButton,
  InputAdornment,
  TextField,
  Alert,
  Typography,
  Collapse,
} from "@mui/material";
import { Link, useNavigate } from "react-router-dom";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useFormik } from "formik";
import * as yup from "yup";
import { jwtDecode } from "jwt-decode";
import { useSelector } from "react-redux";

import { routePaths } from "@/config";
import { useAppDispatch } from "@/store";
import {
  getMemberInfos,
  getToken,
  updateMemberProfile,
} from "@/store/reducers/member/member.actions";
import { TMemberInfos } from "@/features/profile/types";
import { selectEmail } from "@/store/reducers/temp_data/temp_data.selector";
import { TMemberProfile, TSignIn } from "../../types";
import { apiLoginMember } from "../../api/auth.api";
import CModalBanned from "@/components/Modal-Banned/ModalBanned";
import "./SigninForm.styles.scss";

const SigninForm = () => {
  const email_from_redux = useSelector(selectEmail);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isBannished, setIsBannished] = useState(false);
  const [reason, setReason] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Validation du formulaire
  const validationSchema = yup.object({
    identifier: yup.string().required("L'email est obligatoire"),
    password: yup.string().required("Le mot de passe est obligatoire"),
  });

  const formik = useFormik({
    initialValues: {
      identifier: email_from_redux || "",
      password: "",
    },
    validationSchema,
    onSubmit: (values) => {
      setErrorMessage("");
      loginSubmit(values);
    },
  });

  const loginSubmit = async (data: TSignIn) => {
    try {
      setLoading(true);
      const trimmedData = { ...data, identifier: data.identifier.trim() };
      const response = await apiLoginMember(trimmedData);

      const token = response.data.accessToken;
      const profile = response.data.profile as TMemberProfile;
      const decoded = jwtDecode(token);

      const infosUser: TMemberInfos = {
        _id: (decoded.sub as any)?._id || "",
        email: (decoded.sub as any)?.email,
        role: (decoded.sub as any)?.role,
        username: (decoded.sub as any)?.username,
        memberId: (decoded.sub as any)?.memberId,
        isVerified: (decoded.sub as any)?.isVerified,
        isInEmailVerificationProcess: (decoded.sub as any)
          ?.isInEmailVerificationProcess,
      };

      dispatch(getToken(token));
      dispatch(getMemberInfos(infosUser));
      dispatch(updateMemberProfile(profile));

      // Redirection post-login
      navigate(routePaths.profileChildren.myAccountAbsolute, {
        replace: false,
      });

      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      if (error?.response?.status === 403) {
        setIsBannished(true);
        setReason(error.response.data.message);
      } else {
        setErrorMessage(
          error?.response?.data?.message ||
            "Une erreur est survenue lors de la connexion."
        );
      }
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <>
      <div className="signin-container">
        {/* --- En-tête --- */}
        <div className="header-part">
          <h1 className="title">Se Connecter</h1>
          <p className="subtitle">ACCÉDER À L'ESPACE MEMBRE</p>
        </div>

        {/* --- Formulaire --- */}
        <form className="form-part" onSubmit={formik.handleSubmit}>
          {/* Erreur globale (alerte) */}
          {errorMessage && (
            <Collapse
              in={errorMessage !== ""}
              sx={{ width: "100%", marginBottom: "20px" }}
            >
              <Alert
                severity="error"
                sx={{ width: "100%" }}
                onClose={() => setErrorMessage("")}
              >
                {errorMessage}
              </Alert>
            </Collapse>
          )}

          {/* Champ email */}
          <TextField
            fullWidth
            placeholder="Votre adresse email"
            variant="outlined"
            label="Email"
            name="identifier"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.identifier}
            error={
              formik.touched.identifier && Boolean(formik.errors.identifier)
            }
            helperText={formik.touched.identifier && formik.errors.identifier}
          />

          {/* Champ mot de passe */}
          <TextField
            fullWidth
            placeholder="Votre mot de passe"
            variant="outlined"
            label="Mot de passe"
            name="password"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.password}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            type={showPassword ? "text" : "password"}
            sx={{ marginTop: "15px" }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="Afficher/Masquer le mot de passe"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {/* Bouton de connexion */}
          <LoadingButton
            loading={loading}
            loadingPosition="center"
            variant="contained"
            color="tertiary"
            fullWidth
            sx={{ marginTop: "30px" }}
            type="submit"
          >
            SE CONNECTER
          </LoadingButton>
        </form>

        {/* --- Pied de page --- */}
        <div className="footer-part">
          <Link
            className="link-primary"
            to={routePaths.authChildren.forgottenPasswordAbsolute}
          >
            Mot de passe oublié
          </Link>
          <Typography variant="body2">
            Vous n'avez pas de compte ?
            <Link
              className="link-primary"
              to={routePaths.authChildren.signUpAbsolute}
            >
              {" "}
              S'inscrire
            </Link>
          </Typography>
          <a
            href="https://zawaj-sounnah.com/"
            target="_blank"
            rel="noreferrer"
            className="link-gray"
          >
            Aller sur zawaj-sounnah.com
          </a>
        </div>
      </div>

      {/* Modal de bannissement */}
      {isBannished && (
        <CModalBanned
          open={isBannished}
          close={() => setIsBannished(false)}
          reason={reason}
        />
      )}
    </>
  );
};

export default SigninForm;
